import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useAuth } from "@/common/hooks/useAuth";
import { useUpdateUserPreference } from "@/common/services/users";

/**
 * Shared "set active scope" mutation used by:
 *  - ActiveWorkspaceCard (UserProfile page) — cascading Project/Workspace/Version picker
 *  - WorkspaceCardsPane "Set as active" CTA — quick switch on a workspace card
 *  - ProjectExplorer switcher (Projects settings) — grouped project → workspace picker
 *
 * Wraps `useUpdateUserPreference` to merge new ids onto the existing user
 * Pass `soft: false` to force a hard reload (matches the
 * original ProjectSwitcher — safest invalidation for surfaces whose queries
 * were keyed by the scope captured at app boot).
 *
 * By default (`soft: true`), this uses a fast, flicker-free switch: it optimistically updates
 * the cached preference and invalidates scope-dependent queries
 * so they refetch ONCE with the new scope — no full SPA re-bootstrap.
 */
export interface ActiveScopeUpdate {
  projectId?: number | string;
  workspaceId?: number | string;
  versionId?: number | string;
}

export interface UseSetActiveScopeOptions {
  /** Fired after the network mutation resolves successfully, BEFORE the refresh. */
  onSuccess?: () => void;
  /** Fired if the mutation errors. The refresh is skipped on error. */
  onError?: (err: unknown) => void;
  /** Disable the hard reload (default: false). Useful for tests. */
  skipReload?: boolean;
  /**
   * Refresh via React Query invalidation instead of a full page reload
   * (default: true). Much faster/smoother — no atom-spinner re-bootstrap.
   */
  soft?: boolean;
}

export function useSetActiveScope(options: UseSetActiveScopeOptions = {}) {
  const { userPreferenceData, refetchUserPreference } = useAuth();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useUpdateUserPreference(refetchUserPreference);

  const setActiveScope = useCallback(
    (update: ActiveScopeUpdate) => {
      const merged: Record<string, any> = {
        ...(userPreferenceData?.data ?? {}),
        ...(update.projectId != null ? { projectId: update.projectId } : {}),
        ...(update.workspaceId != null
          ? { workspaceId: update.workspaceId }
          : {}),
        ...(update.versionId != null ? { versionId: update.versionId } : {}),
      };
      // Do NOT send activeOrganizationId during scope/version changes — the server
      // already maintains active_organization_id, and sending a potentially stale
      // client-side activeOrganizationId could clobber the DB's active org.
      delete merged.activeOrganizationId;

      mutate(merged, {
        onSuccess: async () => {
          options.onSuccess?.();
          if (options.skipReload) return;

          const isSoft = options.soft ?? true;
          if (isSoft) {
            // Optimistically flip the cached preference so every consumer of
            // `useAuth().userPreference` re-renders with the new scope instantly,
            // then invalidate so scope-dependent queries refetch once. No reload.
            queryClient.setQueriesData(
              { queryKey: ["userPreference"] },
              (prev: any) =>
                prev
                  ? { ...prev, data: { ...(prev.data ?? {}), ...merged } }
                  : prev,
            );

            // We rely on React Query to naturally fetch new data when query keys change
            // as a result of components receiving the new userPreference payload.
            return;
          }

          window.location.reload();
        },
        onError: (err) => {
          options.onError?.(err);
        },
      });
    },
    [
      mutate,
      userPreferenceData?.data,
      options,
      queryClient,
      refetchUserPreference,
    ],
  );

  return { setActiveScope, isApplying: isPending };
}

export default useSetActiveScope;
